/**
 * Provider + hook so feature pages can populate the global StatusBar zones
 * without prop-drilling. A page mounts a child component, calls
 * `useStatusBar().set({...})`, and the shell-level <StatusBar /> renders the
 * three zones reactively.
 *
 * Pages MUST call `set({ visible: false })` (or simply not call set) on
 * unmount — handled here automatically via the same effect cleanup.
 *
 * Example:
 *
 *   useEffect(() => {
 *     setStatusBar({
 *       visible: true,
 *       contextSummary: <span>1,287 items · 12 manufacturers</span>,
 *       totals: <ProjectTotalsInline project={project} />,
 *       state: <SaveIndicator state={saveState} />,
 *     })
 *     return () => clearStatusBar()
 *   }, [project, saveState])
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

export interface StatusBarSlots {
  visible: boolean
  contextSummary?: ReactNode
  totals?: ReactNode
  state?: ReactNode
  /** Optional collapsed center content for <1280px viewports. */
  collapsedCenter?: ReactNode
}

const EMPTY: StatusBarSlots = { visible: false }

interface ContextValue extends StatusBarSlots {
  set: (slots: StatusBarSlots) => void
  clear: () => void
}

const StatusBarContext = createContext<ContextValue>({
  ...EMPTY,
  set: () => {
    /* no-op until provider mounts */
  },
  clear: () => {
    /* no-op */
  },
})

interface ProviderProps {
  children: ReactNode
}

export const StatusBarProvider = ({ children }: ProviderProps): JSX.Element => {
  const [slots, setSlots] = useState<StatusBarSlots>(EMPTY)

  const set = useCallback((next: StatusBarSlots) => {
    setSlots({ ...next, visible: next.visible })
  }, [])

  const clear = useCallback(() => {
    setSlots(EMPTY)
  }, [])

  const value = useMemo<ContextValue>(
    () => ({ ...slots, set, clear }),
    [slots, set, clear],
  )

  return <StatusBarContext.Provider value={value}>{children}</StatusBarContext.Provider>
}

export const useStatusBar = (): ContextValue => useContext(StatusBarContext)
